import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * AppShowcase — rotates four Pendli app screenshots inside an Android phone frame.
 * Order follows the natural app flow: STEG 1 → 2 → 3 → 4.
 * Cycles every 4 seconds with a soft crossfade.
 */
const DEFAULT_SLIDES = [
  { src: "/screenshots/1.png", alt: "Pendli-app – STEG 1 av 4: Fortell oss om plassen" },
  { src: "/screenshots/2.png", alt: "Pendli-app – STEG 2 av 4: Inntektskalkulator og pris" },
  { src: "/screenshots/3.png", alt: "Pendli-app – STEG 3 av 4: Tilgjengelighet" },
  { src: "/screenshots/4.png", alt: "Pendli-app – STEG 4 av 4: Publiser og avbestillingsregler" },
];

const INTERVAL = 4000;

export default function AppShowcase({ className = "", slides }) {
  const SLIDES = slides && slides.length ? slides : DEFAULT_SLIDES;
  const [i, setI] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setI((p) => (p + 1) % SLIDES.length), INTERVAL);
    return () => clearInterval(t);
  }, [SLIDES.length]);

  return (
    <div
      className={`relative mx-auto ${className}`}
      data-testid="app-showcase"
      aria-label="Forhåndsvisning av Pendli-appen"
    >
      {/* Amber glow behind */}
      <div
        aria-hidden="true"
        className="absolute -inset-8 pointer-events-none"
        style={{
          background:
            "radial-gradient(closest-side, rgba(232,168,0,0.22) 0%, rgba(232,168,0,0) 70%)",
        }}
      />

      {/* Android phone frame */}
      <div
        className="relative mx-auto"
        style={{
          width: "min(320px, 78vw)",
          aspectRatio: "9 / 19.5",
          background: "#0D0D0D",
          borderRadius: 44,
          padding: 10,
          boxShadow:
            "0 0 0 1.5px rgba(232,168,0,0.35), 0 30px 60px -20px rgba(0,0,0,0.9), 0 8px 20px rgba(0,0,0,0.6)",
        }}
      >
        {/* Inner bezel */}
        <div
          className="relative w-full h-full overflow-hidden"
          style={{ borderRadius: 34, background: "#F5F0E8" }}
        >
          {/* Center punch-hole camera */}
          <div
            aria-hidden="true"
            className="absolute left-1/2 -translate-x-1/2 z-20"
            style={{
              top: 10,
              width: 10,
              height: 10,
              background: "#0D0D0D",
              borderRadius: "50%",
            }}
          />

          {/* Screenshot slides */}
          <div className="absolute inset-0">
            <AnimatePresence mode="sync">
              <motion.img
                key={SLIDES[i].src}
                src={SLIDES[i].src}
                alt={SLIDES[i].alt}
                loading="eager"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.55, ease: "easeInOut" }}
                className="absolute inset-0 w-full h-full object-contain"
                style={{ background: "#F5F0E8" }}
                data-testid={`app-showcase-slide-${i + 1}`}
              />
            </AnimatePresence>
          </div>
        </div>

        {/* Side buttons (subtle) */}
        <span
          aria-hidden="true"
          className="absolute"
          style={{
            left: -2,
            top: "22%",
            width: 3,
            height: 60,
            background: "#1a1a1a",
            borderRadius: 2,
          }}
        />
        <span
          aria-hidden="true"
          className="absolute"
          style={{
            right: -2,
            top: "26%",
            width: 3,
            height: 90,
            background: "#1a1a1a",
            borderRadius: 2,
          }}
        />
      </div>

      {/* Progress dots */}
      <div className="mt-5 flex items-center justify-center gap-2" data-testid="app-showcase-dots">
        {SLIDES.map((_, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() => setI(idx)}
            aria-label={`Vis skjermbilde ${idx + 1}`}
            data-testid={`app-showcase-dot-${idx + 1}`}
            className="h-1.5 transition-all"
            style={{
              width: idx === i ? 24 : 8,
              background: idx === i ? "#E8A800" : "rgba(245,240,232,0.25)",
            }}
          />
        ))}
      </div>
    </div>
  );
}
